import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, HelpCircle, LogOut, User, Crown, Menu } from "lucide-react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { useNotifications } from "@/hooks/useNotifications";

interface UserData {
  name: string;
  email: string;
  avatar?: string | null;
  plan_name: string;
  total_campaign_limit: number;
  get_remaining_campaigns: number;
  extra_launch_credits: number;
  campaigns_sent_this_month?: number;
  rollover_credits: number;
  is_in_free_trial?: boolean;
  is_in_active_trial?: boolean;
  trial_campaigns_limit?: number;
  trial_campaigns_sent?: number;
  remaining_trial_campaigns?: number;
}

interface PageHeaderProps {
  pageTitle?: string;
  title?: string;
  welcomeMessage?: string;
  description?: string;
  subtitle?: string;
  mobileMenuOffset?: boolean;
  onMenuClick?: () => void;
  currentStep?: number;
  onLogoutAttemptOnStep8?: () => void;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  pageTitle,
  title,
  welcomeMessage,
  description,
  subtitle,
  mobileMenuOffset = false,
  onMenuClick,
  currentStep,
  onLogoutAttemptOnStep8,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(() => {
    return localStorage.getItem("hideUpgradeBanner") !== "true";
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const { unreadCount, fetchUnreadCount } = useNotifications();

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async (showSpinner = false) => {
      try {
        if (showSpinner) setLoading(true);
        const res = await axiosInstance.get(endpoints.me);
        if (!isMounted) return;
        const data = res.data;

        const fullName =
          data.first_name && data.last_name
            ? `${data.first_name} ${data.last_name}`
            : data.first_name || data.last_name || data.email.split("@")[0] || "User";

        const avatarUrl = data.avatar && typeof data.avatar === "string" ? data.avatar : null;

        setUser({
          name: fullName.trim(),
          email: data.email || "",
          avatar: avatarUrl,
          plan_name: data.plan_name || "Free",
          total_campaign_limit: data.total_campaign_limit || 0,
          get_remaining_campaigns: data.get_remaining_campaigns ?? data.plan_period_remaining ?? 0,
          extra_launch_credits: data.extra_launch_credits || 0,
          campaigns_sent_this_month: data.campaigns_sent_this_month || 0,
          rollover_credits: data.rollover_credits || 0,
          is_in_free_trial: data.is_in_free_trial,
          is_in_active_trial: data.is_in_active_trial,
          trial_campaigns_limit: data.trial_campaigns_limit ?? 2,
          trial_campaigns_sent: data.trial_campaigns_sent ?? 0,
          remaining_trial_campaigns: data.remaining_trial_campaigns,
        });
      } catch (err: any) {
        console.error("Failed to fetch user:", err);
        if (err?.response?.status === 401) {
          localStorage.clear();
          if (location.pathname !== "/signup") {
            navigate("/signup", { replace: true });
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Initial load (with spinner)
    fetchUser(true);

    // Re-fetch silently when the tab regains focus (catches mid-session admin changes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchUser(false);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Re-fetch silently every 5 minutes while the tab is open
    const interval = setInterval(() => fetchUser(false), 5 * 60 * 1000);

    const handleUserUpdate = () => fetchUser(false);
    window.addEventListener("user-updated", handleUserUpdate);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
      window.removeEventListener("user-updated", handleUserUpdate);
    };
  }, [navigate, location.pathname]);

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    fetchUnreadCount();
  };

  const getInitial = (name: string) => name?.trim()?.charAt(0)?.toUpperCase() || "U";

  const handleLogout = () => {
    if (currentStep === 8 && onLogoutAttemptOnStep8) {
      onLogoutAttemptOnStep8();
      return;
    }
    localStorage.clear();
    sessionStorage.clear();
    navigate("/signup", { replace: true });
  };

  const isProUser = user?.plan_name === "Pro" || user?.plan_name === "Pro+";
  const isInTrial = Boolean(user?.is_in_active_trial ?? user?.is_in_free_trial);
  const trialLimit = user?.trial_campaigns_limit ?? 2;
  const remainingTrial = user?.remaining_trial_campaigns ?? 0;

  const resolvedTitle = title || pageTitle;
  const resolvedSubtitle = subtitle || description || welcomeMessage;

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90">
      <div className="flex min-h-[72px] w-full flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-4 md:flex-nowrap md:px-6 md:py-0">

        {/* LEFT — Hamburger + Page Title */}
        <div className="min-w-0 flex-1 flex items-center gap-3">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="lg:hidden flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-gray-900 sm:text-lg">
              {resolvedTitle}
            </h1>
            {resolvedSubtitle && (
              <p className="mt-0.5 hidden truncate text-sm text-gray-500 md:block">
                {resolvedSubtitle}
              </p>
            )}
          </div>
        </div>

        {/* CENTER — Upgrade Banner */}
        {showUpgradeBanner && (
          <div className="hidden items-center gap-3 rounded-full bg-white px-4 py-2 shadow-[0_0_0_1px_rgba(251,146,60,0.25),0_4px_12px_rgba(251,146,60,0.18)] xl:flex">
            <span className="text-sm text-gray-700">View or upgrade your plan</span>
            <Button
              size="sm"
              className="h-7 px-3 rounded-full bg-white border border-gray-200 text-gray-900 hover:bg-gray-50"
              onClick={() => navigate("/settings?tab=subscription")}
            >
              View Plans →
            </Button>
            <button
              className="ml-1 text-gray-400 hover:text-gray-600 transition"
              onClick={() => {
                setShowUpgradeBanner(false);
                localStorage.setItem("hideUpgradeBanner", "true");
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* RIGHT — Plan Badge + Icons + Avatar */}
        <div className="flex w-full items-center justify-between gap-2 sm:justify-end md:w-auto md:gap-3">
          {loading ? (
            <div style={{
              height: "34px",
              width: "280px",
              borderRadius: "999px",
              background: "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)",
              backgroundSize: "800px 100%",
              animation: "shimmer 1.4s infinite linear",
            }} />
          ) : (
            <div style={{
              display: "inline-flex",
              flexWrap: "wrap",
              alignItems: "center",
              rowGap: "4px",
              minWidth: 0,
              minHeight: "38px",
              maxWidth: "100%",
              borderRadius: "999px",
              overflow: "hidden",
              backgroundColor: "#2563EB",
              color: "#fff",
              fontSize: "12px",
              fontWeight: 400,
              letterSpacing: "0.01em",
              padding: "5px 0",
            }}>
              <div className="border-none" style={{ display: "flex", alignItems: "center", gap: "5px", paddingRight: "2px", paddingLeft: "12px", height: "100%", whiteSpace: "nowrap" }}>
                {/* <Crown style={{ width: "13px", height: "13px", strokeWidth: 1.5 }} /> */}
                <span>{user?.plan_name || "Free"} Plan</span>
                <span style={{ marginLeft: 2 }}>|</span>
              </div>
              {isInTrial ? (
                <div style={{ display: "flex", alignItems: "center", gap: "5px", padding: "0 12px", height: "100%", whiteSpace: "nowrap" }}>
                  <strong style={{ fontWeight: 700 }}>{remainingTrial}</strong>
                  <span>/ {trialLimit} remaining</span>
                </div>
              ) : (
                <>
                  <div className="border-none" style={{ display: "flex", alignItems: "center", gap: "5px", paddingRight: "2px", paddingLeft: "12px", height: "100%", whiteSpace: "nowrap", flexShrink: 0 }}>
                    {/* <Rocket style={{ width: "13px", height: "13px", strokeWidth: 1.5 }} /> */}
                    <span>Launches:</span>
                    <strong style={{ fontWeight: 700 }}>{user?.get_remaining_campaigns ?? 0}</strong>
                    <span style={{ marginLeft: 2 }}>|</span>
                  </div>
                  <div className="border-none" style={{ display: "flex", alignItems: "center", gap: "5px", padding: "0 12px", height: "100%", whiteSpace: "nowrap", minWidth: 0, flexWrap: "wrap" }}>
                    {/* <ReceiptText style={{ width: "13px", height: "13px", strokeWidth: 1.5 }} /> */}
                    <span style={{ minWidth: 0 }}>Refund Launches:</span>
                    <strong style={{ fontWeight: 700 }}>{user?.extra_launch_credits ?? 0}</strong>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Help + Notifications + Avatar */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-full border border-transparent hover:bg-gray-100"
              onClick={() => navigate("/help")}
            >
              <HelpCircle className="h-5 w-5 text-gray-600" />
            </button>

            {!loading && isProUser && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications((prev) => !prev)}
                  className="relative flex h-9 w-9 items-center justify-center rounded-full border border-transparent transition-colors hover:bg-gray-100"
                >
                  <Bell className="h-5 w-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <NotificationsPanel onClose={handleCloseNotifications} />
                )}
              </div>
            )}

            {loading ? (
              <Skeleton className="h-9 w-9 rounded-full" />
            ) : (
              <DropdownMenu modal={false} open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0">
                    <Avatar className="h-9 w-9 ring-2 ring-white shadow-sm">
                      <AvatarImage src={user?.avatar || undefined} alt={user?.name} />
                      <AvatarFallback className="bg-gray-200 text-gray-700">
                        {getInitial(user?.name || "U")}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => {
                    setProfileMenuOpen(false);
                    navigate("/settings?tab=profile");
                  }}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => {
                    setProfileMenuOpen(false);
                    navigate("/settings?tab=subscription");
                  }}>
                    <Crown className="mr-2 h-4 w-4" />
                    Upgrade Plan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => {
                    setProfileMenuOpen(false);
                    handleLogout();
                  }} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

      </div>
    </header>
  );
};

export default PageHeader;